import React, { useMemo } from 'react';
import { IonButton, IonCheckbox, IonText } from '@ionic/react';
import ActionIconButton from './ActionIconButton';

export interface BulkActionsBarAction {
  id: string;
  label: string;
  icon: string;
  group?: number;
  order?: number;
  disabled?: boolean;
  loading?: boolean;
  requiresConfirm?: boolean;
  confirm?: {
    header: string;
    message: string;
    confirmLabel?: string;
    cancelLabel?: string;
  };
  onExecute: () => Promise<void> | void;
}

interface BulkActionsBarProps {
  selectedCount: number;
  totalCount: number;
  onSelectAll: (checked: boolean) => void;
  actions: BulkActionsBarAction[];
  groupLabels?: Record<number, string>;
}

const BulkActionsBar: React.FC<BulkActionsBarProps> = ({
  selectedCount,
  totalCount,
  onSelectAll,
  actions,
  groupLabels = {},
}) => {
  const [pendingAction, setPendingAction] = React.useState<BulkActionsBarAction | null>(null);
  const sortedActions = useMemo(() => actions
    .map((action, index) => ({ action, index }))
    .sort((left, right) => {
      const groupDifference = (left.action.group ?? 0) - (right.action.group ?? 0);
      if (groupDifference !== 0) return groupDifference;

      const orderDifference = (left.action.order ?? 0) - (right.action.order ?? 0);
      return orderDifference !== 0 ? orderDifference : left.index - right.index;
    })
    .map(({ action }) => action), [actions]);

  if (actions.length === 0) {
    return null;
  }

  const hasVisibleItems = totalCount > 0;
  const allSelected = hasVisibleItems && selectedCount === totalCount;
  const isIndeterminate = selectedCount > 0 && selectedCount < totalCount;
  const handleActionClick = (action: BulkActionsBarAction) => {
    if (action.requiresConfirm ?? true) {
      setPendingAction(action);
      return;
    }

    void action.onExecute();
  };

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', flexWrap: 'nowrap', overflowX: 'auto', margin: '0px' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginRight: '4rem' }}>
        <IonCheckbox
          checked={allSelected}
          indeterminate={isIndeterminate}
          disabled={!hasVisibleItems}
          onIonChange={(event) => onSelectAll(event.detail.checked)}
          aria-label="Select all visible line items"
        />
        { selectedCount > 0 && (
          <IonText style={{ fontSize: '0.85rem', fontWeight: '200', color: 'var(--ion-color-dark)' }}>
            <strong>{selectedCount}</strong> selected
          </IonText>
        )}
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'nowrap' }}>
        {pendingAction ? (
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', whiteSpace: 'nowrap' }}>
            <IonText style={{ fontSize: '0.8rem', color: 'var(--ion-color-dark)' }}>
              {pendingAction.confirm?.message ?? `Confirm ${pendingAction.label}?`}
            </IonText>
            <IonButton
              fill="outline"
              size="small"
              color="medium"
              onClick={() => setPendingAction(null)}
            >
              {pendingAction.confirm?.cancelLabel ?? 'Cancel'}
            </IonButton>
            <IonButton
              fill="solid"
              size="small"
              color="primary"
              onClick={() => {
                const action = pendingAction;
                setPendingAction(null);
                void action.onExecute();
              }}
            >
              {pendingAction.confirm?.confirmLabel ?? 'Confirm'}
            </IonButton>
          </div>
        ) : sortedActions.map((action, index) => {
          const currentGroup = action.group ?? 0;
          const previousGroup = index > 0 ? (sortedActions[index - 1].group ?? 0) : null;
          const isGroupStart = previousGroup === null || previousGroup !== currentGroup;
          const groupLabel = groupLabels[currentGroup];

          return (
            <React.Fragment key={action.id}>
              {isGroupStart && previousGroup !== null && (
                <div
                  aria-hidden="true"
                  style={{ width: '1px', height: '36px', alignSelf: 'center', background: 'var(--ion-color-light-shade)' }}
                />
              )}
              {isGroupStart && groupLabel && (
                <IonText
                  style={{
                    fontSize: '0.7rem',
                    fontWeight: '200',
                    color: 'var(--ion-color-medium)',
                    whiteSpace: 'nowrap',
                    width: '5rem',
                  }}
                >
                  {groupLabel}
                </IonText>
              )}
              <ActionIconButton
                icon={action.icon}
                label={action.label}
                // size="compact"
                disabled={action.disabled}
                loading={action.loading}
                onClick={() => handleActionClick(action)}
                buttonStyle={{ color: 'var(--ion-color-primary)', margin: 0 }}
              />
            </React.Fragment>
          );
        })}
      </div>
    </div>
  );
};

export default BulkActionsBar;